import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { formatAppDate, parseAppDate } from '../../utils/dateFormat';
import PageHeader from '../../components/PageHeader';
import { ordClientsApi } from '../../services/api';
import { CabinetTariff, CreateOrdClientResponse, OrdClient, OrdProvider, StartPricingMode } from '../../types';

const tariffLabel: Record<CabinetTariff, string> = {
  START: 'Старт',
  BUSINESS: 'Бизнес',
};

const START_PACKAGE_SIZE = 31;
const START_PACKAGE_PRICE = 1000;
const START_CREATIVE_PRICE = 40;
const START_MAX_PACKAGES = 18;
const START_MAX_SINGLE_CREATIVES = 30;

const providerLabel: Record<OrdProvider, string> = {
  VK: 'VK ОРД',
  YANDEX: 'Яндекс ОРД',
  OZON: 'Ozon ОРД',
};

const providerCreateHint: Record<OrdProvider, string> = {
  VK: 'Токен VK ОРД можно указать сейчас или позже в настройках ЛК клиента.',
  YANDEX: 'Токен Яндекс ОРД можно указать сейчас или позже в настройках ЛК клиента.',
  OZON: 'Токен Ozon ОРД можно указать сейчас или позже в настройках ЛК клиента.',
};

const ClientsPage: React.FC = () => {
  const [items, setItems] = useState<OrdClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creds, setCreds] = useState<CreateOrdClientResponse | null>(null);
  const [renewClient, setRenewClient] = useState<OrdClient | null>(null);
  const [renewTariff, setRenewTariff] = useState<CabinetTariff>('START');
  const [renewMode, setRenewMode] = useState<StartPricingMode>('PACKAGES');
  const [renewQuantity, setRenewQuantity] = useState(1);
  const [renewMonths, setRenewMonths] = useState(1);
  const [renewPaidUntil, setRenewPaidUntil] = useState('');
  const [editClient, setEditClient] = useState<OrdClient | null>(null);
  const [editName, setEditName] = useState('');
  const [editInn, setEditInn] = useState('');
  const [editContactName, setEditContactName] = useState('');
  const [editStatsSubmission, setEditStatsSubmission] = useState(true);
  const [editAutoReport, setEditAutoReport] = useState(true);

  const BUSINESS_FULL_PRICE = 3000;
  const BUSINESS_LITE_PRICE = 1500;

  const [name, setName] = useState('');
  const [inn, setInn] = useState('');
  const [contactName, setContactName] = useState('');
  const [tariff, setTariff] = useState<CabinetTariff>('START');
  const [ordProvider, setOrdProvider] = useState<OrdProvider>('VK');
  const [ordToken, setOrdToken] = useState('');
  const [subscriptionPaidUntil, setSubscriptionPaidUntil] = useState('');
  const [login, setLogin] = useState('');
  const [allowSelfAd, setAllowSelfAd] = useState(true);
  const [autoReportEnabled, setAutoReportEnabled] = useState(true);
  const [statsSubmissionEnabled, setStatsSubmissionEnabled] = useState(true);
  const [startPricingMode, setStartPricingMode] = useState<StartPricingMode>('PACKAGES');
  const [startPricingQuantity, setStartPricingQuantity] = useState(1);
  const [renewStatsSubmission, setRenewStatsSubmission] = useState(true);

  const canLiteBusiness = tariff === 'BUSINESS' && (ordProvider === 'YANDEX' || ordProvider === 'OZON');
  const effectiveStats = canLiteBusiness ? statsSubmissionEnabled : true;
  const businessCreatePrice = effectiveStats ? BUSINESS_FULL_PRICE : BUSINESS_LITE_PRICE;

  const startMaxQuantity = startPricingMode === 'PACKAGES' ? START_MAX_PACKAGES : START_MAX_SINGLE_CREATIVES;
  const clampedStartQuantity = Math.min(Math.max(startPricingQuantity, 1), startMaxQuantity);
  const startCalculatedCreatives = startPricingMode === 'PACKAGES'
    ? clampedStartQuantity * START_PACKAGE_SIZE
    : clampedStartQuantity;
  const startCalculatedPrice = startPricingMode === 'PACKAGES'
    ? clampedStartQuantity * START_PACKAGE_PRICE
    : clampedStartQuantity * START_CREATIVE_PRICE;

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await ordClientsApi.list());
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setName('');
    setInn('');
    setContactName('');
    setTariff('START');
    setOrdProvider('VK');
    setOrdToken('');
    setSubscriptionPaidUntil('');
    setLogin('');
    setAllowSelfAd(true);
    setAutoReportEnabled(true);
    setStatsSubmissionEnabled(true);
    setStartPricingMode('PACKAGES');
    setStartPricingQuantity(1);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Укажите название клиента');
      return;
    }
    if (tariff === 'START' && (!startPricingMode || clampedStartQuantity < 1)) {
      setError('Для тарифа «Старт» укажите пакеты или креативы');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const res = await ordClientsApi.create({
        name: name.trim(),
        inn: inn.trim() || undefined,
        contactName: contactName.trim() || undefined,
        tariff,
        ordProvider: tariff === 'BUSINESS' ? ordProvider : undefined,
        ordToken: tariff === 'BUSINESS' && ordToken.trim() ? ordToken.trim() : undefined,
        subscriptionPaidUntil: subscriptionPaidUntil || undefined,
        startPricingMode: tariff === 'START' ? startPricingMode : undefined,
        startPricingQuantity: tariff === 'START' ? clampedStartQuantity : undefined,
        autoReportEnabled,
        statsSubmissionEnabled: tariff === 'BUSINESS' ? effectiveStats : undefined,
        login: login.trim() || undefined,
        allowSelfAd,
      });
      setCreds(res);
      setCreateOpen(false);
      resetForm();
      setSuccess(`Клиент «${res.client.name}» создан`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка создания');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (client: OrdClient) => {
    try {
      setError(null);
      await ordClientsApi.update(client.ordClientId, { enabled: !client.enabled });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка обновления');
    }
  };

  const resetPassword = async (client: OrdClient) => {
    if (!window.confirm(`Сбросить пароль администратора клиента «${client.name}»?`)) return;
    try {
      setError(null);
      const res = await ordClientsApi.resetPassword(client.ordClientId);
      setCreds(res);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка сброса пароля');
    }
  };

  const openRenew = (client: OrdClient) => {
    setRenewClient(client);
    setRenewTariff(client.tariff);
    setRenewMode(client.startPricingMode || 'PACKAGES');
    setRenewStatsSubmission(client.statsSubmissionEnabled !== false);
    setRenewQuantity(client.startPricingQuantity || 1);
    setRenewMonths(1);
    setRenewPaidUntil(
      client.subscriptionPaidUntil
        ? (parseAppDate(client.subscriptionPaidUntil)?.add(1, 'month').format('YYYY-MM-DD')
          || dayjs().add(1, 'month').format('YYYY-MM-DD'))
        : dayjs().add(1, 'month').format('YYYY-MM-DD')
    );
  };

  const openEdit = (client: OrdClient) => {
    setEditClient(client);
    setEditName(client.name || '');
    setEditInn(client.inn || '');
    setEditContactName(client.contactName || '');
    setEditStatsSubmission(client.statsSubmissionEnabled !== false);
    setEditAutoReport(client.autoReportEnabled !== false);
  };

  const editLiteAvailable = !!editClient
    && editClient.tariff === 'BUSINESS'
    && (editClient.ordProvider === 'YANDEX' || editClient.ordProvider === 'OZON');
  const editStatsEffective = editLiteAvailable ? editStatsSubmission : true;
  const editBusinessPrice = editStatsEffective ? BUSINESS_FULL_PRICE : BUSINESS_LITE_PRICE;

  const handleEdit = async () => {
    if (!editClient) return;
    if (!editName.trim()) {
      setError('Укажите название клиента');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await ordClientsApi.update(editClient.ordClientId, {
        name: editName.trim(),
        inn: editInn.trim(),
        contactName: editContactName.trim(),
        autoReportEnabled: editAutoReport,
        ...(editLiteAvailable ? { statsSubmissionEnabled: editStatsEffective } : {}),
      });
      setSuccess(`Клиент «${editName.trim()}» обновлён`);
      setEditClient(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка обновления');
    } finally {
      setSaving(false);
    }
  };

  const renewMaxQuantity = renewMode === 'PACKAGES' ? START_MAX_PACKAGES : START_MAX_SINGLE_CREATIVES;
  const clampedRenewQuantity = Math.min(Math.max(renewQuantity || 1, 1), renewMaxQuantity);
  const renewLiteAvailable = renewTariff === 'BUSINESS'
    && renewClient
    && (renewClient.ordProvider === 'YANDEX' || renewClient.ordProvider === 'OZON');
  const renewStatsEffective = renewLiteAvailable ? renewStatsSubmission : true;
  const renewBusinessPrice = renewStatsEffective ? BUSINESS_FULL_PRICE : BUSINESS_LITE_PRICE;
  const renewPrice = renewTariff === 'BUSINESS'
    ? renewBusinessPrice * Math.max(renewMonths, 1)
    : renewMode === 'PACKAGES'
      ? clampedRenewQuantity * START_PACKAGE_PRICE
      : clampedRenewQuantity * START_CREATIVE_PRICE;

  const handleRenew = async () => {
    if (!renewClient) return;
    try {
      setSaving(true);
      setError(null);
      await ordClientsApi.renew(renewClient.ordClientId, {
        subscriptionPaidUntil: renewPaidUntil || undefined,
        months: renewPaidUntil ? undefined : renewMonths,
        tariff: renewTariff,
        startPricingMode: renewTariff === 'START' ? renewMode : undefined,
        startPricingQuantity: renewTariff === 'START' ? clampedRenewQuantity : undefined,
        statsSubmissionEnabled: renewTariff === 'BUSINESS' ? renewStatsEffective : undefined,
      });
      setSuccess(`Подписка клиента «${renewClient.name}» продлена`);
      setRenewClient(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка продления');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client: OrdClient) => {
    if (!window.confirm(`Удалить клиента «${client.name}»? Доступ в ЛК будет закрыт.`)) return;
    try {
      setError(null);
      await ordClientsApi.delete(client.ordClientId);
      setSuccess(`Клиент «${client.name}» удалён`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка удаления');
    }
  };

  return (
    <Box>
      <PageHeader
        title="Клиенты ТрендиОРД"
        description="SaaS-клиенты: тариф «Старт» (VK через Тренди) или «Бизнес» (свой токен ОРД)."
        actions={
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            Добавить клиента
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Клиент</TableCell>
                <TableCell>ИНН</TableCell>
                <TableCell>Тариф</TableCell>
                <TableCell>ОРД</TableCell>
                <TableCell>Подписка до</TableCell>
                <TableCell>Админ</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Клиентов пока нет
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {items.map((c) => (
                <TableRow key={c.ordClientId}>
                  <TableCell>
                    <Typography fontWeight={600}>{c.name}</Typography>
                    {c.contactName && (
                      <Typography variant="caption" color="text.secondary">{c.contactName}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{c.inn || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={tariffLabel[c.tariff]} color={c.tariff === 'START' ? 'primary' : 'secondary'} />
                    {c.tariff === 'BUSINESS' && c.statsSubmissionEnabled === false && (
                      <Chip size="small" label="без стат." variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>{c.ordProvider ? providerLabel[c.ordProvider] : '—'}</TableCell>
                  <TableCell>
                    {c.subscriptionPaidUntil
                      ? formatAppDate(c.subscriptionPaidUntil)
                      : 'без срока'}
                  </TableCell>
                  <TableCell>{c.adminLogin || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={c.enabled !== false ? 'активен' : 'отключён'}
                      color={c.enabled !== false ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                      <Button size="small" onClick={() => openEdit(c)}>Изменить</Button>
                      <Button size="small" onClick={() => openRenew(c)}>Продлить</Button>
                      <Button size="small" onClick={() => resetPassword(c)}>Сброс пароля</Button>
                      <Button size="small" onClick={() => toggleEnabled(c)}>
                        {c.enabled !== false ? 'Отключить' : 'Включить'}
                      </Button>
                      <Button size="small" color="error" onClick={() => handleDelete(c)}>Удалить</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Новый клиент</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Название *" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
            <TextField label="ИНН" value={inn} onChange={(e) => setInn(e.target.value)} fullWidth />
            <TextField label="Контакт" value={contactName} onChange={(e) => setContactName(e.target.value)} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Тариф</InputLabel>
              <Select label="Тариф" value={tariff} onChange={(e) => setTariff(e.target.value as CabinetTariff)}>
                <MenuItem value="START">Старт (VK через Тренди)</MenuItem>
                <MenuItem value="BUSINESS">Бизнес (свой токен ОРД)</MenuItem>
              </Select>
            </FormControl>
            {tariff === 'BUSINESS' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>ОРД</InputLabel>
                  <Select label="ОРД" value={ordProvider} onChange={(e) => setOrdProvider(e.target.value as OrdProvider)}>
                    <MenuItem value="VK">VK ОРД</MenuItem>
                    <MenuItem value="YANDEX">Яндекс ОРД</MenuItem>
                    <MenuItem value="OZON">Ozon ОРД</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Токен ОРД"
                  value={ordToken}
                  onChange={(e) => setOrdToken(e.target.value)}
                  fullWidth
                  type="password"
                  helperText={providerCreateHint[ordProvider]}
                />
                {canLiteBusiness && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={statsSubmissionEnabled}
                          onChange={(e) => setStatsSubmissionEnabled(e.target.checked)}
                        />
                      }
                      label="Сдача статистики через TrendyORD"
                    />
                    <Alert severity="info">
                      {statsSubmissionEnabled
                        ? `Полный «Бизнес»: ${BUSINESS_FULL_PRICE.toLocaleString('ru-RU')} ₽/мес — включая автосдачу статистики.`
                        : `«Бизнес» без статистики: ${BUSINESS_LITE_PRICE.toLocaleString('ru-RU')} ₽/мес. Акты и креативы — у нас; статистику сдаёт клиент в ЛК ОРД (у Ozon — шаблоны автосдачи).`}
                    </Alert>
                  </>
                )}
              </>
            )}
            {tariff === 'START' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Модель оплаты</InputLabel>
                  <Select
                    label="Модель оплаты"
                    value={startPricingMode}
                    onChange={(e) => setStartPricingMode(e.target.value as StartPricingMode)}
                  >
                    <MenuItem value="PACKAGES">Пакеты (31 креатив)</MenuItem>
                    <MenuItem value="CREATIVES">Поштучно</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={startPricingMode === 'PACKAGES' ? 'Количество пакетов' : 'Количество креативов'}
                  type="number"
                  value={clampedStartQuantity}
                  onChange={(e) => setStartPricingQuantity(Number(e.target.value) || 1)}
                  fullWidth
                  inputProps={{
                    min: 1,
                    max: startMaxQuantity,
                  }}
                  helperText={
                    startPricingMode === 'PACKAGES'
                      ? `Лимит: ${startCalculatedCreatives} креативов, ${startCalculatedPrice.toLocaleString('ru-RU')} руб./мес.`
                      : `Лимит: ${startCalculatedCreatives} креативов, ${startCalculatedPrice.toLocaleString('ru-RU')} руб./мес.`
                  }
                />
                <Alert severity="warning">
                  Лимит действует в биллинговом периоде (с 7-го по 6-е число). Неиспользованные креативы сгорают.
                </Alert>
              </>
            )}
            <TextField
              label="Подписка оплачена до"
              type="date"
              value={subscriptionPaidUntil}
              onChange={(e) => setSubscriptionPaidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Логин администратора"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              fullWidth
              helperText="Если пусто — сгенерируется автоматически"
            />
            <FormControlLabel
              control={<Switch checked={allowSelfAd} onChange={(e) => setAllowSelfAd(e.target.checked)} />}
              label="Разрешить саморекламу"
            />
            <FormControlLabel
              control={<Switch checked={autoReportEnabled} onChange={(e) => setAutoReportEnabled(e.target.checked)} />}
              label="Автосдача (акты / по тарифу — статистика) 1-го числа"
            />
            {tariff === 'BUSINESS' && (
              <Typography variant="caption" color="text.secondary">
                Стоимость «Бизнес»: {businessCreatePrice.toLocaleString('ru-RU')} ₽/мес
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>Отмена</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(creds)} onClose={() => setCreds(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Данные администратора клиента</DialogTitle>
        <DialogContent>
          {creds && (
            <Stack spacing={1.25} sx={{ pt: 1 }}>
              <Alert severity="warning">
                Сохраните пароль сейчас. Повторно посмотреть его нельзя, только сбросить.
              </Alert>
              <Typography variant="body2">
                Клиент: <strong>{creds.client.name}</strong>
              </Typography>
              <Typography variant="body2">
                Логин: <strong>{creds.login}</strong>
              </Typography>
              <Typography variant="body2">
                Пароль: <strong>{creds.password}</strong>
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreds(null)}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editClient)} onClose={() => !saving && setEditClient(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Изменить клиента{editClient ? `: ${editClient.name}` : ''}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Название *"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
            />
            <TextField
              label="ИНН"
              value={editInn}
              onChange={(e) => setEditInn(e.target.value)}
              fullWidth
            />
            <TextField
              label="Контакт"
              value={editContactName}
              onChange={(e) => setEditContactName(e.target.value)}
              fullWidth
            />
            {editClient && (
              <Typography variant="body2" color="text.secondary">
                Тариф: {tariffLabel[editClient.tariff]}
                {editClient.ordProvider ? ` · ${providerLabel[editClient.ordProvider]}` : ''}
              </Typography>
            )}
            {editLiteAvailable && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editStatsSubmission}
                      onChange={(e) => setEditStatsSubmission(e.target.checked)}
                    />
                  }
                  label="Сдача статистики через TrendyORD"
                />
                <Alert severity="info">
                  {editStatsSubmission
                    ? `Полный «Бизнес»: ${BUSINESS_FULL_PRICE.toLocaleString('ru-RU')} ₽/мес — включая автосдачу статистики.`
                    : `«Бизнес» без статистики: ${BUSINESS_LITE_PRICE.toLocaleString('ru-RU')} ₽/мес. Акты и креативы — у нас; статистику сдаёт клиент в ЛК ОРД.`}
                  {' '}Тариф к учёту: <strong>{editBusinessPrice.toLocaleString('ru-RU')} ₽/мес</strong>.
                </Alert>
              </>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={editAutoReport}
                  onChange={(e) => setEditAutoReport(e.target.checked)}
                />
              }
              label="Автосдача (акты / по тарифу — статистика) 1-го числа"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditClient(null)} disabled={saving}>Отмена</Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(renewClient)} onClose={() => !saving && setRenewClient(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Продлить подписку{renewClient ? `: ${renewClient.name}` : ''}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Тариф</InputLabel>
              <Select label="Тариф" value={renewTariff} onChange={(e) => setRenewTariff(e.target.value as CabinetTariff)}>
                <MenuItem value="START">Старт</MenuItem>
                <MenuItem value="BUSINESS">Бизнес</MenuItem>
              </Select>
            </FormControl>
            {renewTariff === 'START' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Модель оплаты</InputLabel>
                  <Select label="Модель оплаты" value={renewMode} onChange={(e) => setRenewMode(e.target.value as StartPricingMode)}>
                    <MenuItem value="PACKAGES">Пакеты</MenuItem>
                    <MenuItem value="CREATIVES">Поштучно</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={renewMode === 'PACKAGES' ? 'Количество пакетов' : 'Количество креативов'}
                  type="number"
                  value={clampedRenewQuantity}
                  onChange={(e) => setRenewQuantity(Number(e.target.value) || 1)}
                  fullWidth
                  inputProps={{ min: 1, max: renewMaxQuantity }}
                />
              </>
            )}
            {renewTariff === 'BUSINESS' && (
              <>
                <TextField
                  label="Месяцев"
                  type="number"
                  value={renewMonths}
                  onChange={(e) => setRenewMonths(Math.max(1, Number(e.target.value) || 1))}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
                {renewLiteAvailable && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={renewStatsSubmission}
                        onChange={(e) => setRenewStatsSubmission(e.target.checked)}
                      />
                    }
                    label="Сдача статистики через TrendyORD"
                  />
                )}
              </>
            )}
            <TextField
              label="Оплачено до"
              type="date"
              value={renewPaidUntil}
              onChange={(e) => setRenewPaidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              helperText="Можно задать дату вручную"
            />
            <Alert severity="info">
              Сумма к учёту: <strong>{renewPrice.toLocaleString('ru-RU')} ₽</strong>
              {renewTariff === 'BUSINESS'
                ? ` (${renewBusinessPrice.toLocaleString('ru-RU')} ₽ × ${renewMonths} мес.${!renewStatsEffective ? ', без статистики' : ''})`
                : ''}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewClient(null)} disabled={saving}>Отмена</Button>
          <Button variant="contained" onClick={handleRenew} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Продлить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientsPage;
